import { Alert } from '@/global/components/Alert'
import IconLink from '@/global/components/Button/IconLink'
import Card from '@/global/components/Card'
import { DateField } from '@/global/components/DateField'
import IconeAjuda from '@/global/components/IconeAjuda'
import Skeleton from '@/global/components/Skeleton'
import { TextField } from '@/global/components/TextField'
import Grid from '@/global/components/ui/Grid'
import { useAuth } from '@/hooks'
import { cn } from '@/lib/utils'
import { CategoriaEnum, separateWords } from '@acme/auth'
import { Origens, OrigensEnum } from '@acme/common'
import { SN, StatusAprovacao } from '@acme/core'
import dayjs from 'dayjs'
import PreviewAnexo, { isImage } from 'global/components/Anexo'
import {
    FormDatePicker,
    FormField,
    FormFieldTime,
} from 'global/components/Form'
import { DownloadIcon } from 'lucide-react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Endereco, local, Registro, registros } from 'service/api'
import { uploads } from 'service/api/endpoints/uploads'
import { match } from 'ts-pattern'

const Mapa = lazy(() => import('global/components/Map/Mapa'))

interface IExtraInfo {
    endereco_real?: string
}

export function FormularioDetalhesRegistro() {
    const {
        decoded: { categoria },
    } = useAuth()
    const { watch, setValue } = useFormContext<Registro & IExtraInfo>()
    const [urlFacial, setUrlFacial] = useState<string>()

    const {
        latitude,
        longitude,
        statusAprovacao,
        transactionID,
        empresaId,
        metodoRegistro: metodo,
        fileUrl: anexoRegistro,
        Local,
        ...registro
    } = watch()
    const metodoRegistro = metodo?.trim() || undefined

    const exibirMapa =
        !!String(latitude ?? '').length && !!String(longitude ?? '').length
    useEffect(() => {
        if (exibirMapa && !registro.endereco_real)
            getGeocodeByLatLng(+latitude!, +longitude!).then(address => {
                if (!new RegExp(/-?\d+\.\d+,\s?-?\d+\.\d+/).test(address))
                    setValue('endereco_real', address)
                else
                    setValue(
                        'endereco_real',
                        `https://www.google.com/maps/search/?api=1&query=${address.replace(
                            /\s/,
                            '',
                        )}`,
                    )
            })
    }, [exibirMapa])

    useEffect(() => {
        if (transactionID && urlFacial === undefined)
            registros
                .get<string>(`/imagem/${transactionID}`, { empresaId })
                .then(res => setUrlFacial(res))
    }, [transactionID])

    const mensagem =
        categoria === CategoriaEnum.Funcionario
            ? 'você pode excluir este registro'
            : 'este registro pode ser excluído pelo funcionário'

    return (
        <Grid container>
            {statusAprovacao === StatusAprovacao.REPROVADO &&
                !registro.deletedAt && (
                    <Grid item xs={12}>
                        <Alert severity="error" className="w-full">
                            Este registro foi reprovado e não será considerado
                            para cálculo de ponto. Além disso, {mensagem} a
                            qualquer momento sem requerer justificativa.
                        </Alert>
                    </Grid>
                )}
            <Grid item xs={12}>
                <StatusCerca registro={{ ...registro, Local }} />
            </Grid>
            {!!registro.deletedAt && <DetalhesExclusao />}
            <Grid item xs={4}>
                <FormDatePicker name="data" label="Data" type="date" readonly />
            </Grid>
            <Grid item xs={2}>
                <FormFieldTime name="hora" label="Hora" readOnly />
            </Grid>
            <Grid item xs={6}>
                <FormField name="origem" label="Origem" readOnly />
            </Grid>
            {!!metodoRegistro && (
                <Grid item xs={12}>
                    <TextField
                        name="metodo_registro"
                        label="Método de registro"
                        variant="filled"
                        value={separateWords(metodoRegistro)
                            .replace(/_/g, ' ')
                            .replace(/^\w/, c => c.toUpperCase())}
                        readOnly
                    />
                </Grid>
            )}
            {match(statusAprovacao)
                .with(StatusAprovacao.PENDENTE, () => (
                    <Grid item xs={12}>
                        <FormField
                            name="justificativa"
                            label="Motivo da inclusão"
                            multiline
                            readOnly
                        />
                    </Grid>
                ))
                .with(StatusAprovacao.PENDENTE_EXCLUSAO, () => (
                    <Grid item xs={12}>
                        <FormField
                            name="MotivoExclusao.motivo"
                            label="Motivo da exclusão"
                            multiline
                            readOnly
                        />
                    </Grid>
                ))
                .with(StatusAprovacao.REPROVADO, () => (
                    <Grid item xs={12}>
                        <FormField
                            name="justificativa"
                            label="Motivo da reprovação"
                            multiline
                            readOnly
                        />
                    </Grid>
                ))
                .otherwise(
                    () =>
                        !!registro.justificativa?.trim() && (
                            <Grid item xs={12}>
                                <FormField
                                    name="justificativa"
                                    label="Justificativa"
                                    multiline
                                    readOnly
                                />
                            </Grid>
                        ),
                )}
            {!!Local && (
                <Grid item xs={12} className="flex">
                    <div className="flex-[9.5]">
                        <FormField
                            name="Local.nomeLocal"
                            label="Local para registro"
                            readOnly
                            endAdornment={
                                !!Local.endereco && (
                                    <IconeAjuda forceEnabled>
                                        {Local?.endereco}
                                    </IconeAjuda>
                                )
                            }
                        />
                    </div>
                    <div className="w-2" />
                    <div className="flex-[2.5]">
                        <TextField
                            label="Distância"
                            unHelperText
                            readOnly
                            value={match(registro.distanciaRegistro)
                                .with('0', () => 'Exata')
                                .with(null, undefined, () => '')
                                .when(
                                    x => Number(x) > 999,
                                    x =>
                                        `${new Intl.NumberFormat('pt-BR', {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 2,
                                        }).format(+x / 1000)} km`,
                                )
                                .otherwise(x => x + ' metros')}
                        />
                    </div>
                </Grid>
            )}
            {exibirMapa && [
                <Grid item xs={12} key="endereco_real">
                    <FormField
                        name="endereco_real"
                        label="Endereço real do registro"
                        readOnly
                    />
                </Grid>,
                <Grid item xs={12} key="mapa">
                    <Card className="p-2">
                        <h6 className="text-lg font-semibold text-center pb-2">
                            Localização
                        </h6>
                        <div className="w-full h-auto flex justify-center">
                            <Suspense fallback={<Skeleton />}>
                                <Mapa
                                    latitude={+latitude!}
                                    longitude={+longitude!}
                                />
                            </Suspense>
                        </div>
                    </Card>
                </Grid>,
            ]}
            {urlFacial && (
                <Grid item xs={12}>
                    <Card className="p-2 flex justify-center items-center flex-col">
                        <h6 className="text-lg font-semibold text-center">
                            Selfie
                        </h6>
                        <img
                            src={urlFacial}
                            alt={String(transactionID)}
                            style={{ width: '16rem', height: 'auto' }}
                            loading="lazy"
                        />
                    </Card>
                </Grid>
            )}
            {!!anexoRegistro && (
                <Grid item xs={12}>
                    <Card className="p-2 flex justify-center items-center flex-col relative">
                        <h6 className="text-lg font-semibold text-center">
                            Anexo
                        </h6>
                        <div
                            className={cn(
                                'w-full',
                                isImage(anexoRegistro.split('.').at(-1)!)
                                    ? 'max-w-[16rem]'
                                    : 'max-w-[36rem]',
                            )}
                        >
                            <PreviewAnexo
                                source={`${uploads.URI}/${anexoRegistro}`}
                                ext={anexoRegistro.split('.').at(-1)!}
                            />
                        </div>
                        <IconLink
                            className="absolute top-0 right-1"
                            title="Baixar anexo"
                            href={`${uploads.URI}/${anexoRegistro}`}
                        >
                            <DownloadIcon />
                        </IconLink>
                    </Card>
                </Grid>
            )}
        </Grid>
    )
}

function DetalhesExclusao() {
    const { watch } = useFormContext()

    const [deletedAt, excluidoPor, motivoExclusao] = watch([
        'deletedAt',
        'excluidoPor',
        'motivoExclusao',
    ])

    return (
        <>
            <Grid item xs={3}>
                <DateField
                    label="Data da exclusão"
                    unHelperText
                    value={deletedAt ? dayjs(deletedAt) : null}
                    readOnly
                />
            </Grid>
            <Grid item xs={9}>
                <TextField
                    label="Excluído pelo usuário"
                    unHelperText
                    defaultValue={excluidoPor}
                    readOnly
                />
            </Grid>
            <Grid item xs={12} className="mb-4">
                <TextField
                    label="Motivo da exclusão"
                    unHelperText
                    multiline
                    defaultValue={motivoExclusao ?? 'Não informado'}
                    readOnly
                />
            </Grid>
        </>
    )
}

async function getGeocodeByLatLng(lat: number, lng: number) {
    return local.endereco
        .get<Endereco>({ latitude: lat, longitude: lng })
        .then(res => res.endereco)
}

function StatusCerca({ registro }: { registro: Partial<Registro> }) {
    const isDentroCerca = (() => {
        if (registro.dentroCerca == null) return null
        if (
            // Alguns registros vêm com a informação em texto
            ![
                OrigensEnum.APLICATIVO_PONTO,
                OrigensEnum.APLICATIVO_PONTO_COLETIVO,
                Origens[7],
                Origens[10],
            ].includes(registro.origem as any)
        )
            return null
        if (!registro.Local?.id) return null
        if (!registro.distanciaRegistro || registro.distanciaRegistro === '0')
            return null
        return SN.isTrue(registro.dentroCerca)
    })()

    if (isDentroCerca === null) return null

    return (
        <Alert
            className={cn(
                'w-full mb-4 text-white [&_svg]:text-white',
                isDentroCerca ? 'bg-green-600' : 'bg-error',
            )}
            severity={isDentroCerca ? 'success' : 'error'}
        >
            {isDentroCerca
                ? 'Dentro da cerca eletrônica'
                : 'Fora da cerca eletrônica'}
        </Alert>
    )
}
